import * as React from 'react'

import { Button, useToggle } from '@factorialco/deprecated-design-system'

import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

import AddFundsModal from 'modules/banking/components/AddFundsModal'
import { accountNumberExtraFieldLabel } from 'modules/banking/lib/bankAccountNumbersHelpers'
import useLegalEntities from 'modules/expenses/hooks/useLegalEntities'
import { BankAccount } from 'modules/expenses/lib/types'

import BaseNotification from '../Base'

interface Props {
  bankAccount: BankAccount
  bankAddress: string
  locale: string
}

const FundsNotification = ({ bankAccount, bankAddress, locale }: Props) => {
  if (bankAccount.availableBalanceCents == 0)
    return (
      <EmptyFundsNotification bankAccount={bankAccount} bankAddress={bankAddress} locale={''} />
    )

  if (bankAccount.availableBalanceCents < bankAccount.lowBalanceThresholdCents)
    return (
      <LowFundsNotification
        currency={bankAccount.currency}
        locale={locale}
        accountNumber={bankAccount.accountNumber}
        lowBalanceThresholdCents={Number(bankAccount.lowBalanceThresholdCents)}
      />
    )

  return null
}

const accountBalance = (account: BankAccount): number => {
  const balance = Number(account.availableBalanceCents)
  return isNaN(balance) ? 0.0 : balance
}

const trackGoToAddFunds = (account: BankAccount) => {
  track(trackingEvents.expenses.gotToAddFunds, {
    source: 'notification_cta',
    balance: accountBalance(account),
    currency: account.currency,
    context: 'company',
  })
}

const EmptyFundsNotification = ({ bankAccount, bankAddress }: Props) => {
  const [isOpen, toggleOpen] = useToggle(false)
  const { mainLegalEntity } = useLegalEntities()
  return (
    <>
      <BaseNotification
        type="warning"
        compact
        description={i18n.t('expenses_v2.alerts.funds.no_funds')}
        action={
          <Button
            type="secondary"
            onClick={() => {
              toggleOpen(true)()
              trackGoToAddFunds(bankAccount)
            }}
          >
            {i18n.t('expenses_v2.alerts.funds.add_funds')}
          </Button>
        }
      />
      {isOpen && (
        <AddFundsModal
          bic={bankAccount.bic || bankAccount.sortCode}
          accountNumber={bankAccount.accountNumber}
          legalEntityName={mainLegalEntity?.legalName}
          onClose={toggleOpen(false)}
          beneficiaryName={bankAccount.beneficiaryName ?? ''}
          extraFieldLabel={accountNumberExtraFieldLabel(bankAccount.accountNumberType) || ''}
          bankAddress={bankAddress}
          mode={'addFunds'}
        />
      )}
    </>
  )
}

const LowFundsNotification = ({
  currency,
  accountNumber,
  lowBalanceThresholdCents,
  locale,
}: {
  currency: string
  accountNumber: string
  lowBalanceThresholdCents: number
  locale: string
}) => {
  const amount = MoneyWithCurrency({
    amount: lowBalanceThresholdCents,
    currency,
    optionalDecimals: true,
    locale,
  })

  return (
    <BaseNotification
      type="warning"
      description={i18n.t('expenses_v2.alerts.funds.low_funds', {
        bank_info: accountNumber,
        min_amount: amount,
      })}
    />
  )
}

export { FundsNotification }
